import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface UseOverflowVisibleCountOptions {
  itemsCount: number;
  availableWidth: number;
  gap: number;
  minVisibleItems: number;
  onMeasure?: (data: {
    visibleCount: number;
    hiddenCount: number;
    totalWidth: number;
    availableWidth: number;
  }) => void;
}

export function useOverflowVisibleCount({
  itemsCount,
  availableWidth,
  gap,
  minVisibleItems,
  onMeasure,
}: UseOverflowVisibleCountOptions) {
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(true);

  const recalculateVisibleCount = useCallback(() => {
    if (!hiddenContainerRef.current || availableWidth <= 0) {
      setVisibleCount(itemsCount);
      setIsCalculating(false);
      return;
    }

    let moreButtonWidth = 80;
    if (moreButtonRef.current) {
      moreButtonWidth = moreButtonRef.current.getBoundingClientRect().width;
    }

    const childElements = hiddenContainerRef.current.children;
    const childWidths = Array.from(childElements).map((el) => el.getBoundingClientRect().width);

    const allItemsWidth = childWidths.reduce((sum, width, index) => {
      return sum + (index > 0 ? gap : 0) + width;
    }, 0);

    const allFitsWithoutMore = allItemsWidth <= availableWidth;

    let totalWidth = 0;
    let count = 0;

    if (allFitsWithoutMore) {
      count = childWidths.length;
      totalWidth = allItemsWidth;
    } else {
      count = Math.min(minVisibleItems, childWidths.length);

      for (let i = 0; i < count; i++) {
        totalWidth += (i > 0 ? gap : 0) + childWidths[i];
      }

      const hasHiddenAfterMin = count < childWidths.length;
      const neededWidthForMin = hasHiddenAfterMin
        ? totalWidth + gap + moreButtonWidth
        : totalWidth;

      if (neededWidthForMin > availableWidth) {
        count = 0;
        totalWidth = 0;
        for (let i = 0; i < childWidths.length; i++) {
          const width = childWidths[i];
          const newTotalWidth = totalWidth + (count > 0 ? gap : 0) + width;
          const willHaveHidden = i < childWidths.length - 1;
          const neededWidth = willHaveHidden
            ? newTotalWidth + gap + moreButtonWidth
            : newTotalWidth;

          if (neededWidth <= availableWidth) {
            totalWidth = newTotalWidth;
            count = i + 1;
          } else {
            break;
          }
        }
      } else {
        for (let i = count; i < childWidths.length; i++) {
          const width = childWidths[i];
          const newTotalWidth = totalWidth + (count > 0 ? gap : 0) + width;

          const willHaveHidden = i < childWidths.length - 1;
          const neededWidth = willHaveHidden
            ? newTotalWidth + gap + moreButtonWidth
            : newTotalWidth;

          if (neededWidth <= availableWidth) {
            totalWidth = newTotalWidth;
            count = i + 1;
          } else {
            break;
          }
        }
      }
    }

    if (onMeasure) {
      onMeasure({
        visibleCount: count,
        hiddenCount: itemsCount - count,
        totalWidth,
        availableWidth,
      });
    }

    setVisibleCount(count);
    setIsCalculating(false);
  }, [availableWidth, gap, itemsCount, minVisibleItems, onMeasure]);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      recalculateVisibleCount();
    });
  }, [recalculateVisibleCount]);

  useEffect(() => {
    if (!hiddenContainerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        recalculateVisibleCount();
      });
    });

    resizeObserver.observe(hiddenContainerRef.current);

    const checkMoreButton = () => {
      if (moreButtonRef.current) {
        resizeObserver.observe(moreButtonRef.current);
      }
    };

    checkMoreButton();

    const checkInterval = setInterval(() => {
      if (moreButtonRef.current) {
        resizeObserver.observe(moreButtonRef.current);
        clearInterval(checkInterval);
      }
    }, 100);

    const handleResize = () => {
      requestAnimationFrame(() => {
        recalculateVisibleCount();
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(checkInterval);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [recalculateVisibleCount]);

  return {
    hiddenContainerRef,
    moreButtonRef,
    visibleCount,
    isCalculating,
  };
}
